import { useEffect, useRef, useState } from 'react'
import RoutesMap from 'route_control/RouteControl'
import { Button, Card } from 'react-bootstrap'
import { ArrowLeft, ChevronRight, Smartphone, UserRound } from 'lucide-react'
import { toast } from 'react-toastify'
import logo from 'assets/images/express-logo.png'
import { ButtonLoader } from 'components/loader'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { phoneLoginSendOtp, phoneLoginVerifyOtp, phoneLoginSelectUser } from '../../../redux/slices/assets'
import { SessionManager, isApiSuccess, apiMessage, TOKEN_COOKIE, USER_COOKIE } from 'utils'

// Portal "Login Via Phone No." (#/mobile-verification):
// mobile -> Generate OTP -> 6 digit OTP (resend after 1 min) -> "Login As" account list -> token
const OTP_LEN = 6
const RESEND_SECONDS = 60

// portal convertToAsterisks: 98******10
const maskMobile = (m) => m?.length === 10 ? `${m.slice(0, 2)}******${m.slice(-2)}` : m

export default function PhoneLoginForm(props) {

    const { onBack } = props

    const [mobile, setMobile] = useState('')
    const [submitted, setSubmitted] = useState(false)
    const [nonce, setNonce] = useState(null)
    const [digits, setDigits] = useState(Array(OTP_LEN).fill(''))
    const [seconds, setSeconds] = useState(0)
    const [users, setUsers] = useState([])
    const [spinner, setSpinner] = useState(false)
    const inputs = useRef([])

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const mobileValid = /^[0-9]{10}$/.test(mobile)
    const otpSent = !!nonce

    useEffect(() => {
        if (seconds <= 0) return
        const id = setTimeout(() => setSeconds(s => s - 1), 1000)
        return () => clearTimeout(id)
    }, [seconds])

    const send_otp = () => {
        setSubmitted(true)
        if (!mobileValid) {
            toast.error('Please Enter Vaild Mobile number', { position: "bottom-right" })
            return
        }
        setSpinner(true)
        dispatch(phoneLoginSendOtp({ mobile })).then((res) => {
            setSpinner(false)
            const payload = res?.payload
            if (isApiSuccess(payload)) {
                toast.success('OTP sent successfully', { position: "bottom-right" })
                setNonce(payload.data.data?.nonce)
                setDigits(Array(OTP_LEN).fill(''))
                setSeconds(RESEND_SECONDS)
                setTimeout(() => inputs.current[0]?.focus(), 50)
            } else {
                toast.error(apiMessage(payload, 'Something went wrong!!..'), { position: "bottom-right" })
            }
        })
    }

    const verify_otp = () => {
        const otp = digits.join('')
        if (otp.length !== OTP_LEN) {
            toast.error('Invalid OTP Provided', { position: "bottom-right" })
            return
        }
        setSpinner(true)
        dispatch(phoneLoginVerifyOtp({ mobile, otp, nonce })).then((res) => {
            setSpinner(false)
            const payload = res?.payload
            if (isApiSuccess(payload)) {
                toast.success(payload.data.responseMessage, { position: "bottom-right" })
                setUsers(payload.data.dataList || [])
            } else {
                setDigits(Array(OTP_LEN).fill(''))
                toast.error(apiMessage(payload, 'Invalid OTP Provided'), { position: "bottom-right" })
            }
        })
    }

    const select_user = (user) => {
        setSpinner(true)
        dispatch(phoneLoginSelectUser({ userId: user.userId, nonce: user.nonce })).then((res) => {
            setSpinner(false)
            const data = res?.payload?.data?.data
            if (data?.status === 200 && data?.accessToken) {
                SessionManager.setCookie(TOKEN_COOKIE, data.accessToken, 24)
                SessionManager.setCookie(USER_COOKIE, JSON.stringify({ userId: data.userId, userName: user.fullName }), 24)
                navigate(RoutesMap.DASHBOARD.path)
                toast.success('Login Success', { position: "bottom-right" })
            } else {
                toast.error(apiMessage(res?.payload, 'Login Failed'), { position: "bottom-right" })
            }
        })
    }

    // ---- OTP boxes: auto move forward / back, paste support ----
    const set_digit = (i, value) => {
        const v = value.replace(/\D/g, '')
        if (v.length > 1) {
            const next = [...digits]
            v.slice(0, OTP_LEN - i).split('').forEach((d, k) => { next[i + k] = d })
            setDigits(next)
            inputs.current[Math.min(i + v.length, OTP_LEN - 1)]?.focus()
            return
        }
        const next = [...digits]
        next[i] = v
        setDigits(next)
        if (v && i < OTP_LEN - 1) inputs.current[i + 1]?.focus()
    }
    const key_down = (i, e) => {
        if (e.key === 'Backspace' && !digits[i] && i > 0) inputs.current[i - 1]?.focus()
        if (e.key === 'Enter') verify_otp()
    }

    const back = () => {
        if (users.length) { setUsers([]); setNonce(null); return }
        if (otpSent) { setNonce(null); return }
        onBack()
    }

    return (
        <Card className="h-100 border-0 shadow" style={{ borderRadius: '16px', backgroundColor: '#ffffff', overflow: 'hidden' }}>
            <div className="px-4 pt-3 pb-2">
                <u className='fw-medium text-muted d-inline-flex align-items-center gap-1' style={{ cursor: 'pointer', fontSize: '13px', textDecorationColor: '#006972' }} onClick={back}>
                    <ArrowLeft size={14} /> {otpSent || users.length ? 'Back' : 'Back to Login'}
                </u>
            </div>

            <Card.Body className='px-4 px-sm-5 pb-4 d-flex flex-column justify-content-center'>
                <div className='text-center mb-3'>
                    <img src={logo} alt="logo" className='img-fluid mb-2' style={{ height: '60px', objectFit: 'contain' }} />
                    <h4 className='fw-bold mb-1' style={{ color: '#001529' }}>{users.length ? 'Login As' : 'Mobile OTP Verification'}</h4>
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                        {users.length ? 'Select the account you want to open' :
                            otpSent ? <>OTP has been sent on Mobile Number <strong>{maskMobile(mobile)}</strong></> :
                                'We will send a one-time password to your registered mobile'}
                    </p>
                </div>

                {users.length > 0 ?
                    <div className="phone_users">
                        {users.map(u => (
                            <button type="button" key={u.userId} className="phone_user" onClick={() => select_user(u)} disabled={spinner}>
                                <span className="phone_user_icon"><UserRound size={18} /></span>
                                <span className="flex-grow-1 text-start">{u.fullName}</span>
                                <ChevronRight size={18} />
                            </button>
                        ))}
                    </div>
                    :
                    !otpSent ?
                        <>
                            <label className='fw-semibold mb-1 text-dark' style={{ fontSize: '13px' }}>Mobile Number</label>
                            <div className={`phone_input ${submitted && !mobileValid ? 'is-invalid' : ''}`}>
                                <span><Smartphone size={16} /> +91</span>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={10}
                                    placeholder="Enter Mobile Number"
                                    value={mobile}
                                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                                    onKeyDown={(e) => e.key === 'Enter' && send_otp()}
                                    autoFocus
                                />
                            </div>
                            {submitted && !mobile && <div className='text-danger mt-1 ms-1' style={{ fontSize: '12px' }}>This field is required</div>}
                            {submitted && mobile && !mobileValid && <div className='text-danger mt-1 ms-1' style={{ fontSize: '12px' }}>Atleast 10 digit required</div>}
                            <Button type='button' className='w-100 login_save_btn shadow text-uppercase fw-bold rounded-3 mt-3' style={{ letterSpacing: '1px' }} onClick={send_otp} disabled={spinner}>
                                {spinner ? <ButtonLoader /> : 'Generate OTP'}
                            </Button>
                        </>
                        :
                        <>
                            <div className="otp_boxes">
                                {digits.map((d, i) => (
                                    <input
                                        key={i}
                                        ref={el => (inputs.current[i] = el)}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={OTP_LEN}
                                        value={d}
                                        onChange={(e) => set_digit(i, e.target.value)}
                                        onKeyDown={(e) => key_down(i, e)}
                                        aria-label={`OTP digit ${i + 1}`}
                                    />
                                ))}
                            </div>
                            <div className="text-center mt-2" style={{ fontSize: '13px' }}>
                                <u
                                    style={{ cursor: seconds > 0 ? 'not-allowed' : 'pointer', color: seconds > 0 ? '#adb5bd' : '#006972' }}
                                    onClick={() => seconds <= 0 && !spinner && send_otp()}>
                                    Resend OTP
                                </u>
                                {seconds > 0 && <span className="text-muted ms-2">00:{String(seconds).padStart(2, '0')}</span>}
                            </div>
                            <Button type='button' className='w-100 login_save_btn shadow text-uppercase fw-bold rounded-3 mt-3' style={{ letterSpacing: '1px' }} onClick={verify_otp} disabled={spinner}>
                                {spinner ? <ButtonLoader /> : 'Verify'}
                            </Button>
                        </>
                }
            </Card.Body>
        </Card>
    )
}
